/**
 * The gate every scene passes, whichever path made it: is the verdict well
 * formed, does the scene solve, and does the bench agree with the answer key.
 *
 * See docs/FeatureDesigns/question-scenes/question-scenes-target.md for the design.
 *
 * Trust is decided here and only here. The compiler's own confidence is
 * recorded beside it and never changes it.
 */
import { benchOf } from './registry';
import { solveScene, withAssumed } from './solve';
import { CANNOT_REASONS, CannotReason, CompileVerdict, Expected, ReadoutDecl, Scene, SceneCheck, Agreement } from './types';

// ── Units ───────────────────────────────────────────────────────────────────

type Dim = 'force' | 'length' | 'energy' | 'acceleration' | 'ratio' | 'power' | 'mass';

const UNITS: Record<string, { dim: Dim; factor: number | 'g' | 'g/1000' }> = {
    n: { dim: 'force', factor: 1 }, kn: { dim: 'force', factor: 1000 }, dyne: { dim: 'force', factor: 1e-5 },
    kgf: { dim: 'force', factor: 'g' }, 'kg-wt': { dim: 'force', factor: 'g' }, 'kg wt': { dim: 'force', factor: 'g' },
    gf: { dim: 'force', factor: 'g/1000' }, 'g-wt': { dim: 'force', factor: 'g/1000' },
    m: { dim: 'length', factor: 1 }, cm: { dim: 'length', factor: 0.01 }, mm: { dim: 'length', factor: 0.001 }, km: { dim: 'length', factor: 1000 },
    j: { dim: 'energy', factor: 1 }, kj: { dim: 'energy', factor: 1000 }, erg: { dim: 'energy', factor: 1e-7 },
    'm/s²': { dim: 'acceleration', factor: 1 }, 'm/s^2': { dim: 'acceleration', factor: 1 }, 'm/s2': { dim: 'acceleration', factor: 1 },
    'ms^-2': { dim: 'acceleration', factor: 1 }, 'm s^-2': { dim: 'acceleration', factor: 1 }, 'cm/s²': { dim: 'acceleration', factor: 0.01 },
    '': { dim: 'ratio', factor: 1 }, '%': { dim: 'ratio', factor: 0.01 }, times: { dim: 'ratio', factor: 1 },
    d: { dim: 'power', factor: 1 }, dioptre: { dim: 'power', factor: 1 }, diopter: { dim: 'power', factor: 1 },
    kg: { dim: 'mass', factor: 1 }, g: { dim: 'mass', factor: 0.001 },
};

const unitOf = (u: string | undefined): (typeof UNITS)[string] | undefined => UNITS[(u ?? '').trim().toLowerCase()];

/** `value` in `from`, expressed in `to`; null when they measure different things. */
export function convert(value: number, from: string | undefined, to: string | undefined, gravity: number): number | null {
    const a = unitOf(from), b = unitOf(to);
    if (!a || !b || a.dim !== b.dim) return null;
    const f = (u: typeof a): number => (u.factor === 'g' ? gravity : u.factor === 'g/1000' ? gravity / 1000 : u.factor);
    return (value * f(a)) / f(b);
}

// ── Comparing one answer ────────────────────────────────────────────────────

const SYNONYMS: Record<string, string> = { upright: 'erect', inverted: 'inverted', 'upside down': 'inverted', enlarged: 'magnified', 'same size': 'same', true: 'yes', false: 'no' };
const norm = (s: string): string => { const t = s.trim().toLowerCase(); return SYNONYMS[t] ?? t; };

const RELATIVE = 0.01;
/** A key stated as "0.4" is honest to ±0.05, but a key stated as "2" is not
 *  honest to ±0.5 — it means 2. Half the last digit, capped at this much. */
const PRECISION_CAP = 0.1;

function halfLastDigit(stated: string | undefined): number {
    const m = stated?.trim().match(/^-?\d*(?:\.(\d+))?/);
    return m ? 0.5 * 10 ** -(m[1]?.length ?? 0) : 0;
}

function compareOne(e: Expected, value: unknown, decl: ReadoutDecl, gravity: number): { agreement: Agreement; note: string } {
    if (e.absent) return { agreement: 'no-key', note: 'the key gives no answer for this' };
    if (value === null || value === undefined) return { agreement: 'disagrees', note: 'the bench has no value for this readout here' };

    if (e.category !== undefined) {
        if (typeof value !== 'string') return { agreement: 'disagrees', note: `the key gives a category; ${decl.key} is a number` };
        const same = norm(e.category) === norm(value);
        return { agreement: same ? 'agrees' : 'disagrees', note: `key "${e.category}", bench "${value}"` };
    }
    if (e.value === undefined) return { agreement: 'no-key', note: 'the key entry has neither a value nor a category' };
    if (typeof value !== 'number') return { agreement: 'disagrees', note: `the key gives a number; the bench says "${value}"` };

    const inReadoutUnit = convert(e.value, e.unit ?? decl.unit, decl.unit, gravity);
    if (inReadoutUnit === null) return { agreement: 'disagrees', note: `cannot compare ${e.unit ?? '(no unit)'} with ${decl.unit}` };
    const halfDigit = convert(halfLastDigit(e.stated), e.unit ?? decl.unit, decl.unit, gravity) ?? 0;
    const scale = Math.abs(inReadoutUnit);
    const tolerance = Math.max(
        RELATIVE * scale,
        decl.tolerance?.absolute ?? 1e-9,
        (decl.tolerance?.relative ?? 0) * scale,
        Math.min(halfDigit, PRECISION_CAP * scale),
    );
    const diff = Math.abs(value - inReadoutUnit);
    const show = (x: number): string => `${+x.toPrecision(4)}${decl.unit ? ` ${decl.unit}` : ''}`;
    return {
        agreement: diff <= tolerance ? 'agrees' : 'disagrees',
        note: `key ${show(inReadoutUnit)}, bench ${show(value)}${diff <= tolerance ? '' : ` (off by ${show(diff)})`}`,
    };
}

// ── The verdict ─────────────────────────────────────────────────────────────

/** Everything wrong with the SHAPE of a compiler's verdict. Empty when well formed. */
export function validateVerdict(v: CompileVerdict): string[] {
    if (!v || typeof v !== 'object') return ['the verdict must be an object'];
    if (v.outcome === 'cannot') {
        const errors: string[] = [];
        if (!CANNOT_REASONS.includes(v.reason)) errors.push(`reason must be one of ${CANNOT_REASONS.join(', ')}`);
        if (typeof v.why !== 'string' || !v.why.trim()) errors.push('why must say, in a line or two, why there is no scene');
        return errors;
    }
    if (v.outcome !== 'scene') return ['outcome must be "scene" or "cannot"'];
    const errors: string[] = [];
    if (!['high', 'medium', 'low'].includes(v.confidence)) errors.push('confidence must be high, medium or low');
    if (typeof v.confidenceWhy !== 'string') errors.push('confidenceWhy must say what the compiler was unsure about');
    if (!['exact', 'approximate'].includes(v.fidelity)) errors.push('fidelity must be exact or approximate');
    if (!Array.isArray(v.expected)) errors.push('expected must be a list — one entry per ask the key answers, or { ask, absent: true }');
    else v.expected.forEach((e, i) => {
        if (!Number.isInteger(e.ask) || e.ask < 0 || e.ask >= (v.scene?.asks?.length ?? 0)) errors.push(`expected[${i}].ask must index scene.asks`);
        if (!e.absent && e.value === undefined && e.category === undefined) errors.push(`expected[${i}] needs a value, a category, or absent: true`);
    });
    return errors;
}

/** Solve the verdict's scene and hold it to the answer key. */
export function checkVerdict(v: CompileVerdict): SceneCheck | { errors: string[] } {
    const shape = validateVerdict(v);
    if (shape.length) return { errors: shape };
    if (v.outcome === 'cannot') return { errors: [] };

    const first = checkOnce(v, v.scene);
    if (!first.agreement.includes('disagrees') || !first.solution.ok) return first;

    /* The key disagrees. If the setup left a convention unstated, the key may
     * simply have used another one — try each, and keep the first under which
     * nothing disagrees. One convention at a time: a key that needs two
     * unstated guesses to agree is not agreeing. */
    const bench = benchOf(v.scene.bench)!;
    for (const u of bench.unstated ?? []) {
        if (getSetupPath(v.scene.setup, u.path) !== undefined) continue;
        for (const alt of u.alternatives) {
            const assumed = { [u.path]: alt };
            const r = checkOnce(v, withAssumed(v.scene, assumed));
            if (r.solution.ok && !r.agreement.includes('disagrees')) {
                return { ...r, assumed, notes: r.notes.map(n => `${n} — assuming ${u.label} = ${alt}, which the question does not state`) };
            }
        }
    }
    return first;
}

const getSetupPath = (o: Record<string, unknown>, path: string): unknown =>
    path.split('.').reduce<unknown>((x, k) => (x && typeof x === 'object' ? (x as Record<string, unknown>)[k] : undefined), o);

function checkOnce(v: Extract<CompileVerdict, { outcome: 'scene' }>, scene: Scene): SceneCheck {
    const solution = solveScene(scene);
    const n = scene.asks?.length ?? 0;
    if (!solution.ok) {
        return { solution, agreement: Array(n).fill('no-key'), notes: solution.errors, trust: 'rejected' };
    }
    const bench = benchOf(scene.bench)!;
    const unstatedG = bench.unstated?.find(u => u.path === 'g');
    const gravity = typeof scene.setup.g === 'number' ? scene.setup.g : unstatedG?.default ?? 9.8;
    const agreement: Agreement[] = [];
    const notes: string[] = [];
    solution.answers.forEach((a, i) => {
        const e = v.expected.find(x => x.ask === i);
        const decl = bench.readouts.find(r => r.key === a.ask.readout)!;
        const r = e ? compareOne(e, a.value, decl, gravity) : { agreement: 'no-key' as const, note: 'the key does not answer this ask' };
        agreement.push(r.agreement);
        notes.push(r.note);
    });
    const trust = agreement.includes('disagrees') ? 'rejected'
        : agreement.includes('no-key') ? 'unverified'
            : 'verified';
    return { solution, agreement, notes, trust };
}

// ── The model's output ──────────────────────────────────────────────────────

/**
 * What a compiler emits, before it is a verdict: ONE flat object, every field
 * present, the ones that do not apply null. Flat because a union at the root
 * of a response schema is where providers disagree; one shape for both paths
 * because the batch and runtime compilers must not drift apart.
 * The canonical JSON schema is `SceneCompileV1` in
 * Backend/functions/src/prompt/typeSchemas/sceneCompile.ts.
 */
export interface SceneCompileOutput {
    outcome: 'scene' | 'cannot';
    reason: CannotReason | null;
    why: string | null;
    scene: Scene | null;
    expected: Expected[] | null;
    confidence: 'high' | 'medium' | 'low' | null;
    confidenceWhy: string | null;
    fidelity: 'exact' | 'approximate' | null;
    approximations: string[] | null;
}

/** The compiler's flat output as a verdict, or what is wrong with it. */
export function verdictFromOutput(raw: unknown): { verdict: CompileVerdict } | { errors: string[] } {
    if (!raw || typeof raw !== 'object') return { errors: ['the output must be a JSON object'] };
    const o = raw as Partial<SceneCompileOutput>;
    let verdict: CompileVerdict;
    if (o.outcome === 'cannot') {
        verdict = { outcome: 'cannot', reason: o.reason as never, why: o.why ?? '' };
    } else if (o.outcome === 'scene') {
        if (!o.scene) return { errors: ['outcome "scene" needs a scene'] };
        const scene: Scene = { ...o.scene, assumptions: o.scene.assumptions ?? [] };
        if (scene.inverse === null) delete scene.inverse;
        scene.asks = (scene.asks ?? []).map(a => Object.fromEntries(Object.entries(a).filter(([, v]) => v !== null)) as unknown as Scene['asks'][number]);
        verdict = {
            outcome: 'scene',
            scene,
            expected: (o.expected ?? []).map(e => Object.fromEntries(Object.entries(e).filter(([, v]) => v !== null)) as unknown as Expected),
            confidence: o.confidence as never,
            confidenceWhy: o.confidenceWhy ?? '',
            fidelity: o.fidelity as never,
            ...(o.approximations?.length ? { approximations: o.approximations } : {}),
        };
    } else {
        return { errors: ['outcome must be "scene" or "cannot"'] };
    }
    const errors = validateVerdict(verdict);
    return errors.length ? { errors } : { verdict };
}
